<template>
  <card-container
    :id="pageId"
    :class="pageId"
    hide-reset
    @submit="onPaymentSubmit"
  >
    <template #title>
      Add Payment Method
    </template>

    <template #content>
      <p class="notification-message">
        Please remind the customer that they will be starting their membership after they add the credit card.
      </p>

      <AddPaymentMethodWrapper
        ref="paymentMethodWrapperRef"
        :home-address="homeAddress"
        :use3ds="use3ds"
        :caller="newCustomerInfo"
        :plan="newCustomerInfo.registerPlan"
        :subscription-start-at="subscriptionStartAt"
        is-registration
        @set-payment-info="setPaymentInfo"
        @card-added="onCardAddedViaLink"
      />

      <insufficient-funds-dialog
        :is-show-dialog="isShowInsufficientFundsDialog"
        @submit="onSetSubscriptionStartAt"
        @cancel="isShowInsufficientFundsDialog = false"
      />

      <p
        v-if="paymentCheckStatus.warningMessage"
        class="notification-message pt-10"
      >
        <b>{{ paymentCheckStatus.warningMessage }}</b>
      </p>
    </template>

    <template #submit-button>
      Add Payment Method{{ use3ds ? ' (with 3ds)' : '' }}
    </template>

    <template #additional-buttons>
      <p-button
        v-if="!partnerCode"
        type="yellow"
        class="btn-unflag-account"
        round
        @click="unflagAccount"
      >
        Unflag Account
      </p-button>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, onMounted, defineAsyncComponent, toRefs, watch } from 'vue';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { useCustomer } from '@/use/customer';
import { useEventListener } from '@/use/eventListener';
import { useDashboard } from '@/use/app';
import { addPaymentSource } from '@/services/payment/addPaymentSource';
import { usePaymentSourceCheck } from '@/use/paymentSourceCheck';
import { StripeDeclinedCode } from '@/services/payments/stripeErrors';
import { callerService } from '@/services/caller';
import type { StripeIntentKind } from '@/use/stripe';

const InsufficientFundsDialog = defineAsyncComponent(() => import('../../Dialogs/InsufficientFunds.vue') as any);

const pageId = 'bill-information';
const InsufficientFundsCode = 'insufficient_funds';

const props = defineProps({
  partnerCode: {
    type: String,
    default: '',
  },
  isGourmetRegistration: {
    type: Boolean,
    default: false,
  },
});
const { isGourmetRegistration } = toRefs(props);

const emit = defineEmits(['change-payment', 'goto-next-step', 'valid']);

const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { newCustomerInfo } = useCustomer();
const { isPartnerDashboard } = useDashboard();
const { status: paymentCheckStatus, clearPaymentSourceCheck, checkCreditCard } = usePaymentSourceCheck();

const paymentMethodWrapperRef: Ref<any> = ref(null);
const homeAddress: Ref<any> = ref({
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
});

const subscriptionStartAt = ref('');
const isShowInsufficientFundsDialog = ref(false);
const use3ds = ref(false);

const reopenManualPaymentDialog = () => {
  paymentMethodWrapperRef.value?.setDialogStatus({
    isShowManualPaymentDialog: true,
  });
};

const setPaymentInfo = async (data: {
  setupIntentId: string;
  confirmationTokenId?: string;
  intentKind?: StripeIntentKind;
  cardHolderType: string;
  use3ds: boolean;
}) => {
  use3ds.value = data.use3ds;
  paymentCheckStatus.cardHolderType = data.cardHolderType;
  paymentCheckStatus.setupIntentId = data.setupIntentId;
  paymentCheckStatus.confirmationTokenId = data.confirmationTokenId;
  paymentCheckStatus.intentKind = data.intentKind ?? 'setup';

  await checkCreditCard({
    setupIntentId: data.setupIntentId,
    confirmationTokenId: data.confirmationTokenId,
    intentKind: paymentCheckStatus.intentKind,
    userId: newCustomerInfo.value.id,
    treatReturnedMessageAsError: true,
    onCheckFailed: reopenManualPaymentDialog,
    onAddAnyway: addCreditCard,
  });
};

const onPaymentSubmit = () => {
  if (paymentCheckStatus.setupIntentId) {
    addCreditCard();
    return;
  }

  showPaymentDialog();
};

const addCreditCard = async () => {
  const postData = {
    setupIntentId: paymentCheckStatus.setupIntentId,
    confirmationTokenId: paymentCheckStatus.confirmationTokenId || undefined,
    intentKind: paymentCheckStatus.intentKind,
    stripeAccountName: newCustomerInfo.value?.stripeAccountName,
    subscriptionStartAt: subscriptionStartAt.value || null,
    userId: newCustomerInfo.value.id,
    isGourmetRegistration: isGourmetRegistration.value,
    plan: isPartnerDashboard.value ? null : newCustomerInfo.value.registerPlan,
    cardHolderType: paymentCheckStatus.cardHolderType,
  };

  const loading = ElLoading.service({
    lock: true,
    text: 'Adding Payment Method...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    await addPaymentSource(postData);

    let successMessage = 'Added credit card successfully';

    if (postData.subscriptionStartAt) {
      successMessage += ` and schedule subscription at ${postData.subscriptionStartAt}. We will try to subscribe a subscription at ${postData.subscriptionStartAt} automatically`;
    }

    await showSuccessPopupDialog({
      text: successMessage,
    });
    emit('change-payment', false);
    emit('goto-next-step', true);
    emit('valid', true);
    clearPaymentSourceCheck();
  } catch (error) {
    // A declined SetupIntent/PaymentIntent confirm consumes its confirmation token; both are
    // single-use. Drop the stale ids so the next "Add Payment Method" click reopens the dialog
    // for a fresh SetupIntent/PaymentIntent + token instead of replaying the consumed one.
    clearPaymentSourceCheck();

    const errorText = util.getGraphqlError(error);
    const errorCode = util.getGraphqlErrorCode(error);

    if (errorCode === InsufficientFundsCode) {
      isShowInsufficientFundsDialog.value = true;
    } else if (errorCode === StripeDeclinedCode.DoNotHonor || errorCode === StripeDeclinedCode.AuthenticationRequired) {
      if (use3ds.value) {
        showErrorPopupDialog({
          text: `The card didn't work even with 3d secure.`,
        });
        use3ds.value = false;
      } else if (
        await showConfirmPopupDialog({
          text: 'Do you want to retry with 3D Secure? Ask the customer.',
          title: 'Card rejected',
          confirmButtonText: 'Retry with 3D Secure',
          cancelButtonText: 'Cancel',
          icon: 'error',
        })
      ) {
        use3ds.value = true;
        paymentMethodWrapperRef.value?.setDialogStatus({
          isShowManualPaymentDialog: true,
        });
      }
    } else {
      showErrorPopupDialog({
        text: errorText,
        type: 'html',
      });
      emit('valid', false);
    }
  } finally {
    loading.close();
  }
};

const onCardAddedViaLink = () => {
  emit('change-payment', false);
  emit('goto-next-step', true);
  emit('valid', true);
};

const onSetSubscriptionStartAt = (date: string) => {
  isShowInsufficientFundsDialog.value = false;

  if (!date) {
    return;
  }

  subscriptionStartAt.value = date;
  paymentMethodWrapperRef.value?.setDialogStatus({
    isShowManualPaymentDialog: true,
  });
};

const unflagAccount = async () => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Unflagging Account...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    await callerService.updateCaller({
      callerId: newCustomerInfo.value.id,
      data: {
        flagged: 0,
      },
    });

    showSuccessPopupDialog({
      text: 'The account has been unflagged successfully.',
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  loading.close();
};

const showPaymentDialog = () => {
  subscriptionStartAt.value = '';

  paymentMethodWrapperRef.value?.setDialogStatus({
    isShowChoosePaymentMethodWayDialog: true,
  });
};

const resetForm = () => {
  homeAddress.value = {
    streetAddress: newCustomerInfo.value.streetAddress || '',
    streetAddress2: newCustomerInfo.value.streetAddress2 || '',
    city: newCustomerInfo.value.city || '',
    state: newCustomerInfo.value.state || '',
    zip: newCustomerInfo.value.zip || '',
    country: newCustomerInfo.value.country || '',
  };
  use3ds.value = false;
  clearPaymentSourceCheck();
};

watch(newCustomerInfo, resetForm, {
  deep: true,
});

useEventListener('reset-registration-data', resetForm);

onMounted(resetForm);
</script>

<style lang="scss">
.bill-information {
  .payment-logo {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    height: 50px;
    img.lock-key {
      height: 35px;
    }
    img.secure-key {
      display: inline-block;
      position: relative;
      top: -20px;
      height: 70px;
    }
    span {
      font-size: 12px;
      @media (max-width: 576px) {
        line-height: 14px;
        display: inline-block;
      }
    }
  }
  .sub-section {
    position: relative;
    button {
      position: absolute;
      top: -14px;
      left: 120px;
      height: 30px;
      line-height: 10px;
      @media (max-width: 576px) {
        top: -10px;
      }
    }
  }
  .form-submit-button-group {
    .btn-unflag-account {
      margin-left: 50px;
      @media (max-width: 576px) {
        margin-left: 0;
      }
    }
    button {
      @media (max-width: 576px) {
        margin-bottom: 5px !important;
      }
    }
  }
}
</style>
